import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal'; // Yeni modalı import et

// Types (User, Role, UserRole) - Bunları ayrı bir types dosyasından import etmek daha iyi olabilir
interface Role {
  _id: string;
  name: string;
  roleCode: string;
}

interface User {
  _id: string;
  username: string;
  isAdmin: boolean;
}

interface UserRole {
  userId: string;
  roleId: Role; // Rol detaylarını içerecek şekilde güncellendi
}

const UserRolesPage: React.FC = () => {
  const { isAdmin } = useContext(AuthContext);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]); // Rol atama için gerekli
  const [userRolesMap, setUserRolesMap] = useState<Map<string, UserRole[]>>(new Map()); // Kullanıcı ID'sine göre rolleri tutacak map

  // Rol atama modalı için state
  const [isAssigningRole, setIsAssigningRole] = useState<boolean>(false);
  const [assigningUser, setAssigningUser] = useState<User | null>(null);
  const [selectedRoleToAssign, setSelectedRoleToAssign] = useState<string>('');

  // Genel state
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true); // Başlangıçta true
  const [loadingRoles, setLoadingRoles] = useState<boolean>(true); // Başlangıçta true
  const [loadingAction, setLoadingAction] = useState<string>(''); // Örn: 'assign-role-userId', 'remove-role-userId-roleId'

  // Arama ve filtreleme için state
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');

  // Confirmation Modal State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalProps, setConfirmModalProps] = useState<{
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
    confirmButtonVariant?: 'primary' | 'danger' | 'warning';
    confirmText?: string;
  }>({
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    if (!isAdmin) return;
    fetchInitialData();
  }, [isAdmin]);

  const fetchInitialData = async () => {
    setLoadingUsers(true);
    setLoadingRoles(true);
    setError('');
    try {
      // Kullanıcıları ve rolleri paralel olarak çek
      const [usersResponse, rolesResponse] = await Promise.all([
        api.get('/users'),
        api.get('/roles?fields=name,roleCode'), // Sadece gerekli alanları çek
      ]);
      setUsers(usersResponse.data);
      setRoles(rolesResponse.data);
      setLoadingRoles(false); // Roller yüklendi

      // Kullanıcı rolleri için map'i hazırla ve istekleri başlat
      const newUserRolesMap = new Map<string, UserRole[]>();
      const userRolePromises = usersResponse.data.map(async (user: User) => {
        try {
          const userRoleResponse = await api.get(`/user-roles/${user._id}`);
          newUserRolesMap.set(user._id, userRoleResponse.data);
        } catch (err) {
          console.error(`Error fetching roles for user ${user.username} (${user._id}):`, err);
          newUserRolesMap.set(user._id, []); // Hata durumunda boş dizi ata
        }
      });

      // Tüm kullanıcı rolleri isteklerinin tamamlanmasını bekle
      await Promise.all(userRolePromises);
      setUserRolesMap(newUserRolesMap);

    } catch (err) {
      console.error("Fetch initial data error:", err);
      setError('Kullanıcı veya rol verileri yüklenirken bir hata oluştu.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingUsers(false); // Kullanıcılar ve rolleri yüklendi
    }
  };

  // Belirli bir kullanıcının rollerini yeniden yükle (atama/kaldırma sonrası)
  const refreshUserRoles = async (userId: string) => {
    try {
      const userRoleResponse = await api.get(`/user-roles/${userId}`);
      setUserRolesMap(prevMap => new Map(prevMap).set(userId, userRoleResponse.data));
    } catch (err) {
      console.error(`Error refreshing roles for user ${userId}:`, err);
      // Hata mesajı gösterilebilir
    }
  };

  // Hata ve başarı mesajlarını temizleme fonksiyonu
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Hata ve başarı mesajlarını ayarlama ve otomatik temizleme
  const setFlashMessage = (type: 'error' | 'success', message: string, duration: number = 4000) => {
    clearMessages();
    if (type === 'error') setError(message);
    if (type === 'success') setSuccess(message);
    setTimeout(clearMessages, duration);
  };

  // Rol atama modalını aç
  const openAssignRoleModal = (user: User) => {
    clearMessages();
    setAssigningUser(user);
    setSelectedRoleToAssign(''); // Seçimi sıfırla
    setIsAssigningRole(true);
  };

  // Rol atama modalını kapat
  const closeAssignRoleModal = () => {
    setIsAssigningRole(false);
    setAssigningUser(null);
    setSelectedRoleToAssign('');
  };

  // Kullanıcıya rol atama işlemi
  const handleAssignRole = async () => {
    if (!assigningUser || !selectedRoleToAssign) {
      setFlashMessage('error', 'Lütfen atanacak bir rol seçin.');
      return;
    }

    // Kullanıcının zaten bu role sahip olup olmadığını kontrol et
    const currentUserRoles = userRolesMap.get(assigningUser._id) || [];
    if (currentUserRoles.some(ur => ur.roleId._id === selectedRoleToAssign)) {
        setFlashMessage('error', `${assigningUser.username} zaten bu role sahip.`);
        closeAssignRoleModal();
        return;
    }


    setLoadingAction(`assign-${assigningUser._id}-${selectedRoleToAssign}`);
    clearMessages();
    try {
      await api.post('/user-roles/assign', { userId: assigningUser._id, roleId: selectedRoleToAssign });
      setFlashMessage('success', `Rol başarıyla ${assigningUser.username} kullanıcısına atandı.`, 3000);
      await refreshUserRoles(assigningUser._id); // Sadece ilgili kullanıcının rollerini güncelle
      closeAssignRoleModal(); // Modalı kapat
    } catch (err: any) {
      console.error("Assign role error:", err);
      const errorMessage = err.response?.data?.message || 'Rol atanırken bir hata oluştu.';
      setFlashMessage('error', errorMessage);
    } finally {
      setLoadingAction('');
    }
  };

  // Kullanıcıdan rol kaldırma işlemini başlatan fonksiyon (Modalı açar)
  const handleRemoveRole = (userId: string, roleId: string, username: string, roleName: string) => {
    setConfirmModalProps({
      title: 'Rol Kaldırma Onayı',
      message: (
        <>
          <strong>'{username}'</strong> kullanıcısından <strong>'{roleName}'</strong> rolünü kaldırmak istediğinizden emin misiniz?
        </>
      ),
      onConfirm: () => executeRemoveRole(userId, roleId, username, roleName), // Onaylandığında çalışacak fonksiyon
      confirmButtonVariant: 'danger',
      confirmText: 'Evet, Kaldır',
    });
    setIsConfirmModalOpen(true);
  };

  // Modal onaylandıktan sonra rol kaldırma işlemini gerçekleştiren fonksiyon
  const executeRemoveRole = async (userId: string, roleId: string, username: string, roleName: string) => {
    setLoadingAction(`remove-${userId}-${roleId}`);
    clearMessages();
    try {
      await api.delete('/user-roles/remove', { data: { userId, roleId } });
      setFlashMessage('success', `Rol başarıyla ${username} kullanıcısından kaldırıldı.`, 3000);
      await refreshUserRoles(userId); // Sadece ilgili kullanıcının rollerini güncelle
    } catch (err: any) {
      console.error("Remove role error:", err);
      const errorMessage = err.response?.data?.message || 'Rol kaldırılırken bir hata oluştu.';
      setFlashMessage('error', errorMessage);
    } finally {
      setLoadingAction('');
      setIsConfirmModalOpen(false); // Modalı kapat
    }
  };

  // Kullanıcının rollerini map'ten al
  const getUserRoles = (userId: string): UserRole[] => {
    return userRolesMap.get(userId) || [];
  };

  // Filtrelenmiş kullanıcılar
  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  // Yetki kontrolü
  if (!isAdmin) {
    return (
      <div className="p-8 bg-red-50 text-red-700 rounded-lg shadow-md flex flex-col items-center">
        {/* ... Erişim Engellendi içeriği ... */}
        <h1 className="text-2xl font-bold mb-2">Erişim Engellendi</h1>
        <p className="text-center">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  // Ana JSX
  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.660.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
        Kullanıcı Rol Yönetimi
      </h1>

      {/* Hata ve Başarı Mesajları */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm flex items-center animate-fade-in" role="alert">
          {/* ... Hata ikonu ve mesajı ... */}
          <p>{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded shadow-sm flex items-center animate-fade-in" role="alert">
          {/* ... Başarı ikonu ve mesajı ... */}
          <p>{success}</p>
        </div>
      )}

      {/* Kullanıcı Listesi */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
            Sistemdeki Kullanıcılar
            <div className="ml-4 px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
              {users.length} kullanıcı
            </div>
          </h2>
          {/* Kullanıcı Arama */}
          <div className="relative">
            <input
              type="text"
              placeholder="Kullanıcı ara..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="border border-gray-300 rounded-full px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Kullanıcı Tablosu */}
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          {(loadingUsers || loadingRoles) && users.length === 0 ? ( // İlk yükleme durumu
            <div className="flex justify-center items-center p-10">
              <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mr-4"></div>
              <span className="text-gray-500">Kullanıcı ve rol verileri yükleniyor...</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center p-6 text-gray-500">
              {userSearchQuery ? (
                <div className="flex flex-col items-center">
                  {/* ... Arama sonucu yok ikonu ve mesajı ... */}
                  <p>"{userSearchQuery}" aramasına uygun kullanıcı bulunamadı.</p>
                  <button onClick={() => setUserSearchQuery('')} className="mt-2 text-indigo-600 hover:underline">Aramayı temizle</button>
                </div>
              ) : (
                <p>Sistemde kayıtlı kullanıcı bulunamadı.</p>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">Atanmış Roller</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const userRolesList = getUserRoles(user._id);
                  const isLoadingThisUserRoles = loadingUsers && !userRolesMap.has(user._id); // Sadece bu kullanıcının rolleri yükleniyorsa

                  return (
                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                      {/* Kullanıcı Bilgisi */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-semibold">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {user.username}
                              {user.isAdmin && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 border border-purple-200 font-medium">
                                  Admin
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">ID: {user._id.substring(user._id.length - 6)}</div>
                          </div>
                        </div>
                      </td>
                      {/* Atanmış Roller */}
                      <td className="px-6 py-4">
                        {isLoadingThisUserRoles ? (
                           <div className="flex items-center text-xs text-gray-500">
                             <div className="h-4 w-4 mr-2 border-t-2 border-b-2 border-gray-400 rounded-full animate-spin"></div>
                             Roller yükleniyor...
                           </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 items-center">
                            {userRolesList.length > 0 ? userRolesList.map((userRole: UserRole) => (
                              <div key={userRole.roleId._id} className="group relative inline-block">
                                <span className="flex items-center px-3 py-1 text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap">
                                  {userRole.roleId.name}
                                  {/* Rol Kaldır Butonu (handleRemoveRole çağırıyor) */}
                                  <button
                                    onClick={() => handleRemoveRole(user._id, userRole.roleId._id, user.username, userRole.roleId.name)} // Modal açan fonksiyonu çağır
                                    disabled={loadingAction.startsWith('remove-')} // Genel kaldırma işlemi sırasında disable
                                    className={`ml-2 text-red-500 hover:text-red-700 ${loadingAction.startsWith('remove-') ? 'opacity-50 cursor-wait' : ''}`}
                                    title={`${userRole.roleId.name} rolünü kaldır`}
                                  >
                                    {/* Buton içindeki loading state kaldırılabilir */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </span>
                                {/* Rol Kodu Tooltip (isteğe bağlı) */}
                                <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 absolute left-0 mt-1 w-auto bg-gray-800 text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                                  {userRole.roleId.roleCode}
                                </div>
                              </div>
                            )) : (
                              <span className="text-xs text-gray-500 italic">Rol atanmamış</span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* İşlemler (Rol Ata Butonu) */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openAssignRoleModal(user)}
                          disabled={loadingRoles} // Roller yüklenmediyse butonu disable et
                          className={`bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-md transition flex items-center text-xs shadow-sm ${loadingRoles ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={`${user.username} kullanıcısına rol ata`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Rol Ata
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Rol Atama Modalı */}
      {isAssigningRole && assigningUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl animate-scale-in">
            {/* Modal Başlığı */}
            <div className="flex justify-between items-center mb-5 border-b pb-3">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Rol Ata: <span className="ml-2 font-medium">{assigningUser.username}</span>
              </h2>
              <button
                onClick={closeAssignRoleModal}
                className="text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                title="Kapat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Rol Seçim Alanı */}
            <div className="mb-5">
              <label htmlFor="roleSelect" className="block text-sm font-medium text-gray-700 mb-1">Atanacak Rolü Seçin</label>
              <select
                id="roleSelect"
                value={selectedRoleToAssign}
                onChange={(e) => setSelectedRoleToAssign(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
              >
                <option value="">-- Rol Seçin --</option>
                {roles
                  .filter(role => !(userRolesMap.get(assigningUser._id) || []).some(ur => ur.roleId._id === role._id)) // Kullanıcının zaten sahip olmadığı rolleri filtrele
                  .map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} ({role.roleCode})
                  </option>
                ))}
              </select>
              {roles.filter(role => !(userRolesMap.get(assigningUser._id) || []).some(ur => ur.roleId._id === role._id)).length === 0 && (
                  <p className="text-xs text-gray-500 mt-1 italic">Bu kullanıcıya atanabilecek başka rol bulunmuyor.</p>
              )}
            </div>

            {/* Modal Eylemleri */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={closeAssignRoleModal}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2 rounded-md transition"
              >
                İptal
              </button>
              <button
                onClick={handleAssignRole}
                disabled={!selectedRoleToAssign || loadingAction.startsWith(`assign-${assigningUser._id}`)}
                className={`bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-6 py-2 rounded-md transition flex items-center shadow-md ${!selectedRoleToAssign || loadingAction.startsWith(`assign-${assigningUser._id}`) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loadingAction.startsWith(`assign-${assigningUser._id}`) ? (
                  <>
                    <div className="h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                    Atanıyor...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Rolü Ata
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onay Modalı */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmModalProps.onConfirm}
        title={confirmModalProps.title}
        message={confirmModalProps.message}
        confirmText={confirmModalProps.confirmText}
        confirmButtonVariant={confirmModalProps.confirmButtonVariant}
        // isLoading={loadingAction.startsWith('remove-')} // İsteğe bağlı: Modalın kendi loading state'i
      />

      {/* Stil Tanımları */}
      <style jsx>{`
        .loader {
          border-top-color: #4F46E5; /* indigo-600 */
          animation: spinner 1.5s linear infinite;
        }
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default UserRolesPage;
